import { createSlice, type PayloadAction } from "@reduxjs/toolkit"

import type { ImportOptions, UploadSummary } from "@/lib/contact-upload"

export type ContactUploadStep = 0 | 1 | 2

export type ContactUploadColumnMappingRow = {
  column_index: number
  source_column: string
  to: string
  include: boolean
  label: string
}

export type ContactUploadSessionStatus =
  | "idle"
  | "uploading"
  | "parsed"
  | "processing"
  | "completed"
  | "failed"

export type ContactUploadHydratedPayload = {
  workspaceId: string
  userId: string
  uploadId: string
  step: ContactUploadStep
  status: ContactUploadSessionStatus
  phase: string
  processed: number
  total: number
  summary?: UploadSummary | null
  errorMessage?: string | null
  importOptions?: ImportOptions | null
  columnMappingRows?: ContactUploadColumnMappingRow[]
}

export type ContactUploadSessionState = {
  status: ContactUploadSessionStatus
  workspaceId: string | null
  userId: string | null
  uploadId: string
  step: ContactUploadStep
  phase: string
  processed: number
  total: number
  summary: UploadSummary | null
  errorMessage: string | null
  importOptions: ImportOptions | null
  columnMappingRows: ContactUploadColumnMappingRow[]
  dialogOpen: boolean
}

const initialState: ContactUploadSessionState = {
  status: "idle",
  workspaceId: null,
  userId: null,
  uploadId: "",
  step: 0,
  phase: "",
  processed: 0,
  total: 0,
  summary: null,
  errorMessage: null,
  importOptions: null,
  columnMappingRows: [],
  dialogOpen: false,
}

type SessionIdentity = {
  workspaceId: string
  userId: string
}

type StartUploadingSessionPayload = SessionIdentity & {
  uploadId: string
  importOptions: ImportOptions
}

type StartParsedSessionPayload = SessionIdentity & {
  uploadId: string
  step?: ContactUploadStep
  importOptions: ImportOptions
  columnMappingRows: ContactUploadColumnMappingRow[]
}

type UpdateProgressPayload = {
  phase: string
  processed: number
  total: number
  summary?: UploadSummary | null
  errorMessage?: string | null
}

const contactUploadSessionSlice = createSlice({
  name: "contactUploadSession",
  initialState,
  reducers: {
    setContactUploadDialogOpen(state, action: PayloadAction<boolean>) {
      state.dialogOpen = action.payload
    },
    startContactUploadUploadingSession(state, action: PayloadAction<StartUploadingSessionPayload>) {
      const { workspaceId, userId, uploadId, importOptions } = action.payload
      state.status = "uploading"
      state.workspaceId = workspaceId
      state.userId = userId
      state.uploadId = uploadId
      state.step = 0
      state.phase = "uploading"
      state.processed = 0
      state.total = 0
      state.summary = null
      state.errorMessage = null
      state.importOptions = importOptions
      state.columnMappingRows = []
    },
    startContactUploadParsedSession(state, action: PayloadAction<StartParsedSessionPayload>) {
      const { workspaceId, userId, uploadId, importOptions, columnMappingRows } = action.payload
      state.status = "parsed"
      state.workspaceId = workspaceId
      state.userId = userId
      state.uploadId = uploadId
      state.step = action.payload.step ?? 1
      state.phase = "parsed"
      state.processed = 0
      state.total = 0
      state.summary = null
      state.errorMessage = null
      state.importOptions = importOptions
      state.columnMappingRows = columnMappingRows
    },
    updateContactUploadMappingRows(
      state,
      action: PayloadAction<ContactUploadColumnMappingRow[]>
    ) {
      state.columnMappingRows = action.payload
    },
    updateContactUploadImportOptions(state, action: PayloadAction<ImportOptions>) {
      state.importOptions = action.payload
    },
    startContactUploadProcessing(state) {
      state.status = "processing"
      state.step = 2
      state.phase = "processing"
      state.processed = 0
      state.total = 0
      state.summary = null
      state.errorMessage = null
    },
    updateContactUploadProgress(state, action: PayloadAction<UpdateProgressPayload>) {
      const { phase, processed, total, summary, errorMessage } = action.payload
      state.phase = phase
      state.processed = processed
      state.total = total
      if (summary !== undefined) {
        state.summary = summary
      }
      if (errorMessage !== undefined) {
        state.errorMessage = errorMessage
      }
      if (phase === "completed") {
        state.status = "completed"
        state.step = 2
      } else if (phase === "failed") {
        state.status = "failed"
        state.step = 2
      } else if (phase === "processing") {
        state.status = "processing"
        state.step = 2
      } else if (phase === "parsed") {
        state.status = "parsed"
      }
    },
    hydrateContactUploadSession(
      state,
      action: PayloadAction<ContactUploadHydratedPayload>
    ) {
      const payload = action.payload
      state.workspaceId = payload.workspaceId
      state.userId = payload.userId
      state.uploadId = payload.uploadId
      state.step = payload.step
      state.status = payload.status
      state.phase = payload.phase
      state.processed = payload.processed
      state.total = payload.total
      state.summary = payload.summary ?? null
      state.errorMessage = payload.errorMessage ?? null
      if (payload.importOptions !== undefined) {
        state.importOptions = payload.importOptions
      }
      if (payload.columnMappingRows !== undefined) {
        state.columnMappingRows = payload.columnMappingRows
      }
    },
    clearContactUploadSession() {
      return initialState
    },
    acknowledgeContactUploadSession(state) {
      if (state.status === "completed" || state.status === "failed") {
        return initialState
      }
      return state
    },
  },
})

export const {
  setContactUploadDialogOpen,
  startContactUploadUploadingSession,
  startContactUploadParsedSession,
  updateContactUploadMappingRows,
  updateContactUploadImportOptions,
  startContactUploadProcessing,
  updateContactUploadProgress,
  hydrateContactUploadSession,
  clearContactUploadSession,
  acknowledgeContactUploadSession,
} = contactUploadSessionSlice.actions

export const contactUploadSessionReducer = contactUploadSessionSlice.reducer

export function selectContactUploadSession(state: {
  contactUploadSession: ContactUploadSessionState
}): ContactUploadSessionState {
  return state.contactUploadSession
}

export function selectContactUploadInProgress(state: {
  contactUploadSession: ContactUploadSessionState
}): boolean {
  const s = state.contactUploadSession
  return s.status === "uploading" || s.status === "processing"
}

export function contactUploadSessionMatchesIdentity(
  session: ContactUploadSessionState,
  workspaceId: string | null | undefined,
  userId: string | null | undefined
): boolean {
  if (!session.uploadId || !workspaceId || !userId) return false
  return session.workspaceId === workspaceId && session.userId === userId
}
